const LOCAL_HEADER = 0x04034b50;
const CENTRAL_HEADER = 0x02014b50;
const END_OF_DIRECTORY = 0x06054b50;
const STORED = 0;
const DEFLATED = 8;

const LENGTH_BASE = [
  3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131, 163, 195, 227, 258,
];
const LENGTH_EXTRA = [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0];
const DISTANCE_BASE = [
  1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049, 3073, 4097, 6145,
  8193, 12289, 16385, 24577,
];
const DISTANCE_EXTRA = [
  0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13,
];
const CODE_LENGTH_ORDER = [16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15];

interface HuffmanTable {
  table: Uint16Array;
  bits: number;
}

function buildTable(lengths: ArrayLike<number>): HuffmanTable {
  let bits = 0;
  for (let i = 0; i < lengths.length; i++) {
    bits = Math.max(bits, lengths[i]);
  }
  const table = new Uint16Array(1 << bits);
  if (!bits) {
    return { table, bits };
  }

  const counts = new Uint16Array(16);
  for (let i = 0; i < lengths.length; i++) {
    counts[lengths[i]]++;
  }
  counts[0] = 0;
  const nextCode = new Uint16Array(16);
  let code = 0;
  for (let length = 1; length <= 15; length++) {
    code = (code + counts[length - 1]) << 1;
    nextCode[length] = code;
  }

  for (let symbol = 0; symbol < lengths.length; symbol++) {
    const length = lengths[symbol];
    if (!length) {
      continue;
    }
    let reversed = 0;
    for (let i = 0, value = nextCode[length]++; i < length; i++, value >>= 1) {
      reversed = (reversed << 1) | (value & 1);
    }
    for (let index = reversed; index < table.length; index += 1 << length) {
      table[index] = (symbol << 4) | length;
    }
  }
  return { table, bits };
}

const FIXED_LITERALS = buildTable(Array.from({ length: 288 }, (_, i) => (i < 144 ? 8 : i < 256 ? 9 : i < 280 ? 7 : 8)));
const FIXED_DISTANCES = buildTable(new Array(30).fill(5));

class Inflater {
  private position = 0;
  private buffer = 0;
  private count = 0;
  private output: Uint8Array;
  private length = 0;

  constructor(
    private readonly input: Uint8Array,
    expectedSize: number,
    private readonly limit: number,
  ) {
    this.output = new Uint8Array(Math.min(Math.max(expectedSize, 1024), limit));
  }

  private need(bits: number): void {
    while (this.count < bits) {
      if (this.position > this.input.length + 4) {
        throw new Error('Повреждённый архив');
      }
      this.buffer |= (this.input[this.position++] ?? 0) << this.count;
      this.count += 8;
    }
  }

  private take(bits: number): number {
    if (!bits) {
      return 0;
    }
    this.need(bits);
    const value = this.buffer & ((1 << bits) - 1);
    this.buffer >>>= bits;
    this.count -= bits;
    return value;
  }

  private decode(huffman: HuffmanTable): number {
    this.need(huffman.bits);
    const entry = huffman.table[this.buffer & ((1 << huffman.bits) - 1)];
    const length = entry & 15;
    if (!length) {
      throw new Error('Повреждённый архив');
    }
    this.buffer >>>= length;
    this.count -= length;
    return entry >> 4;
  }

  private reserve(extra: number): void {
    const required = this.length + extra;
    if (required <= this.output.length) {
      return;
    }
    if (required > this.limit) {
      throw new Error('Файл слишком большой для просмотра');
    }
    const grown = new Uint8Array(Math.min(Math.max(required, this.output.length * 2), this.limit));
    grown.set(this.output.subarray(0, this.length));
    this.output = grown;
  }

  private stored(): void {
    this.position -= this.count >> 3;
    this.buffer = 0;
    this.count = 0;
    const size = this.input[this.position] | (this.input[this.position + 1] << 8);
    this.position += 4;
    if (this.position + size > this.input.length) {
      throw new Error('Повреждённый архив');
    }
    this.reserve(size);
    this.output.set(this.input.subarray(this.position, this.position + size), this.length);
    this.length += size;
    this.position += size;
  }

  private dynamicTables(): [HuffmanTable, HuffmanTable] {
    const literalCount = this.take(5) + 257;
    const distanceCount = this.take(5) + 1;
    const codeCount = this.take(4) + 4;
    const codeLengths = new Uint8Array(19);
    for (let i = 0; i < codeCount; i++) {
      codeLengths[CODE_LENGTH_ORDER[i]] = this.take(3);
    }
    const codeTable = buildTable(codeLengths);

    const lengths = new Uint8Array(literalCount + distanceCount);
    for (let i = 0; i < lengths.length; ) {
      const symbol = this.decode(codeTable);
      if (symbol < 16) {
        lengths[i++] = symbol;
        continue;
      }
      let repeat: number;
      let value = 0;
      if (symbol === 16) {
        if (!i) {
          throw new Error('Повреждённый архив');
        }
        value = lengths[i - 1];
        repeat = 3 + this.take(2);
      } else if (symbol === 17) {
        repeat = 3 + this.take(3);
      } else {
        repeat = 11 + this.take(7);
      }
      if (i + repeat > lengths.length) {
        throw new Error('Повреждённый архив');
      }
      lengths.fill(value, i, i + repeat);
      i += repeat;
    }
    return [buildTable(lengths.subarray(0, literalCount)), buildTable(lengths.subarray(literalCount))];
  }

  private compressed(literals: HuffmanTable, distances: HuffmanTable): void {
    for (;;) {
      const symbol = this.decode(literals);
      if (symbol < 256) {
        this.reserve(1);
        this.output[this.length++] = symbol;
        continue;
      }
      if (symbol === 256) {
        return;
      }
      const lengthIndex = symbol - 257;
      if (lengthIndex >= LENGTH_BASE.length) {
        throw new Error('Повреждённый архив');
      }
      const size = LENGTH_BASE[lengthIndex] + this.take(LENGTH_EXTRA[lengthIndex]);
      const distanceIndex = this.decode(distances);
      if (distanceIndex >= DISTANCE_BASE.length) {
        throw new Error('Повреждённый архив');
      }
      const distance = DISTANCE_BASE[distanceIndex] + this.take(DISTANCE_EXTRA[distanceIndex]);
      if (distance > this.length) {
        throw new Error('Повреждённый архив');
      }
      this.reserve(size);
      for (let i = 0; i < size; i++, this.length++) {
        this.output[this.length] = this.output[this.length - distance];
      }
    }
  }

  run(): Uint8Array {
    let last = 0;
    while (!last) {
      last = this.take(1);
      const type = this.take(2);
      if (type === 0) {
        this.stored();
      } else if (type === 1) {
        this.compressed(FIXED_LITERALS, FIXED_DISTANCES);
      } else if (type === 2) {
        this.compressed(...this.dynamicTables());
      } else {
        throw new Error('Повреждённый архив');
      }
    }
    return this.output.subarray(0, this.length);
  }
}

interface ZipEntry {
  method: number;
  compressedSize: number;
  size: number;
  offset: number;
}

export class ZipArchive {
  private readonly entries = new Map<string, ZipEntry>();
  private readonly view: DataView;
  readonly totalSize: number;

  constructor(
    private readonly bytes: Uint8Array,
    private readonly limit: number,
  ) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    let end = -1;
    for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i--) {
      if (this.view.getUint32(i, true) === END_OF_DIRECTORY) {
        end = i;
        break;
      }
    }
    if (end < 0) {
      throw new Error('Не найдено оглавление zip-архива');
    }

    const count = this.view.getUint16(end + 10, true);
    let offset = this.view.getUint32(end + 16, true);
    const decoder = new TextDecoder('utf-8');
    let total = 0;
    for (let i = 0; i < count; i++) {
      if (this.view.getUint32(offset, true) !== CENTRAL_HEADER) {
        throw new Error('Повреждённый архив');
      }
      const method = this.view.getUint16(offset + 10, true);
      const compressedSize = this.view.getUint32(offset + 20, true);
      const size = this.view.getUint32(offset + 24, true);
      const nameLength = this.view.getUint16(offset + 28, true);
      const extraLength = this.view.getUint16(offset + 30, true);
      const commentLength = this.view.getUint16(offset + 32, true);
      const localOffset = this.view.getUint32(offset + 42, true);
      if (size === 0xffffffff || compressedSize === 0xffffffff || localOffset === 0xffffffff) {
        throw new Error('Архивы zip64 не поддерживаются');
      }

      const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength));
      this.entries.set(name, { method, compressedSize, size, offset: localOffset });
      total += size;
      offset += 46 + nameLength + extraLength + commentLength;
    }
    this.totalSize = total;
  }

  has(path: string): boolean {
    return this.entries.has(path);
  }

  bytesOf(path: string): Uint8Array | null {
    const entry = this.entries.get(path);
    if (!entry) {
      return null;
    }
    if (this.view.getUint32(entry.offset, true) !== LOCAL_HEADER) {
      throw new Error('Повреждённый архив');
    }
    const nameLength = this.view.getUint16(entry.offset + 26, true);
    const extraLength = this.view.getUint16(entry.offset + 28, true);
    const start = entry.offset + 30 + nameLength + extraLength;
    const data = this.bytes.subarray(start, start + entry.compressedSize);
    if (entry.method === STORED) {
      return data;
    }
    if (entry.method !== DEFLATED) {
      throw new Error('Неподдерживаемый способ сжатия');
    }
    return new Inflater(data, entry.size, this.limit).run();
  }

  text(path: string): string | null {
    const bytes = this.bytesOf(path);
    return bytes ? new TextDecoder('utf-8').decode(bytes) : null;
  }
}
